<template>
  <div class="p-4 md:px-10 md:py-6">
    <page-breadcrumb
      :category="$i18nPresenter.t('account')"
      :text="breadcrumbText"
    />

    <div class="white-label-content">
      <aha-section>
        <template #header>
          <h5>{{ $i18nPresenter.t('Your DNS settings') }}</h5>
        </template>
        <template #body>
          <p class="mb-3">
            {{ $i18nPresenter.t("For each subdomain you provide below, please create a CNAME record that points" +
            " to <strong>whitelabel.ahaslides.com</strong>. Please check with your DNS service provider if you're not sure how to.") }}
          </p>
          <p class="mb-2">
            {{ $i18nPresenter.t('For further technical support, please email us at') }}
            <a
              class="hover:text-primary-90"
              href="mailto:hi@ahaslides.com"
            >hi@ahaslides.com</a>.
          </p>
        </template>
      </aha-section>
      <aha-section>
        <template #header>
          <h5>{{ $i18nPresenter.t('Audience subdomain') }}</h5>
        </template>
        <template #body>
          <div>
            <div class="mb-3">
              <label class="block mb-2">{{ $i18nPresenter.t('Enter the subdomain for participant access:') }}</label>
              <aha-input
                type="text"
                required
                :placeholder="placeHolderEGLiveDomain"
                class="form-control"
                id="name"
                v-model="whiteLabelLink"
                @keyup.enter="updateProfile(1)"
              />
            </div>

            <div class="mb-2 text-red-100" v-if="audienceDomainError">
              {{ audienceDomainError }}
            </div>

            <aha-button
              class="mr-2"
              @click="updateProfile(1)"
              :disabled="!whiteLabelLink"
            >
              <span>{{ $i18nPresenter.t('Submit') }}</span>
              <aha-spinner variant="white" v-if="isSubmittingAudienceDomain" class="ml-1" />
            </aha-button>
            <aha-button
              variant="danger"
              @click="showModalDelete(1)"
              v-if="$store.state.currentUser.whiteLabelLink"
            >
              <span>{{ $i18nPresenter.t('Delete') }}</span>
              <aha-spinner variant="white" v-if="isDeletingWhiteLabelAudience" class="ml-1" />
            </aha-button>
          </div>
        </template>
      </aha-section>

      <aha-section>
        <template #header>
          <h5>{{ $i18nPresenter.t('Presenter subdomain') }}</h5>
        </template>
        <template #body>
          <div>
            <div class="mb-3">
              <label for="name-presenter-domain" class="block mb-2">
                {{ $i18nPresenter.t('Enter the subdomain for presenter ' +
                  'access below. This subdomain will also be used for presentation sharing links:') }}
              </label>
              <aha-input
                type="text"
                required
                :placeholder="placeHolderEGPresenter"
                id="name-presenter-domain"
                v-model="whiteLabelPresenterDomain"
                @keyup.enter="updateProfile(2)"
              />
            </div>

            <div class="mb-2 text-red-100" v-if="presenterDomainError">
              {{ presenterDomainError }}
            </div>

            <aha-button
              class="mr-2"
              @click="updateProfile(2)"
              :disabled="!whiteLabelPresenterDomain"
            >
              <span>{{ $i18nPresenter.t('Submit') }}</span>
              <aha-spinner variant="white" v-if="isSubmittingPresenterDomain" class="ml-1" />
            </aha-button>
            <aha-button
              variant="danger"
              @click="showModalDelete(2)"
              v-if="$store.state.currentUser.whiteLabelPresenterDomain"
            >
              <span>{{ $i18nPresenter.t('Delete') }}</span>
              <aha-spinner variant="white" v-if="isDeletingWhiteLabelPresenter" class="ml-1" />
            </aha-button>
          </div>
        </template>
      </aha-section>
    </div>

    <aha-modal
      v-if="isShowCreateWhiteLabelConfirm"
      id="modal-whitelabel-confirm"
      @hide="isShowCreateWhiteLabelConfirm = false"
      action-position="justify-center"
      :title="typeUpdate === 1 ? `${$i18nPresenter.t('Audience subdomain updated')}`
        : `${$i18nPresenter.t('Presenter subdomain updated')}`"
    >
      <p class="overflow-hidden">
        {{ $i18nPresenter.t('Domain updated. Please note that' +
        ' it may takes <strong>up to 48 hours</strong> to take full effect.') }}
      </p>
      <template #action>
        <aha-button
          class="w-16 mr-2"
          @click="ok"
        >
          OK
        </aha-button>
      </template>
    </aha-modal>

    <aha-modal
      title="Delete confirmation"
      id="modal-confirm-delete-white-label"
      v-if="isShowDeleteWhiteLabelModal"
      @hide="isShowDeleteWhiteLabelModal = false"
    >
      <div class="overflow-hidden">
        {{ $i18nPresenter.t('Are you sure you want to delete this subdomain:') }}
        <span>
          <strong v-if="typeDelete === 1">{{ $store.state.currentUser.whiteLabelLink }}</strong>
          <strong v-if="typeDelete === 2">
            {{ $store.state.currentUser.whiteLabelPresenterDomain }}
          </strong>
        </span>
      </div>
      <template #action>
        <aha-link @click="cancelDelete()">
          <span class="font-bold">{{ $i18nPresenter.t('Cancel') }}</span>
        </aha-link>
        <aha-button
          variant="danger"
          class="mr-2"
          @click="confirmDelete()"
          has-margin-left
        >
          {{ $i18nPresenter.t('Delete') }}
        </aha-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import Util from '@/util'

// api
import UserApi from '@/api/user'

// UI
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'

export default {
  components: {
    PageBreadcrumb,
  },
  data() {
    return {
      isSubmittingAudienceDomain: false,
      isSubmittingPresenterDomain: false,
      whiteLabelLink: '',
      audienceDomainError: '',
      presenterDomainError: '',
      whiteLabelPresenterDomain: '',
      typeUpdate: 1,
      isDeletingWhiteLabelAudience: false,
      isDeletingWhiteLabelPresenter: false,
      typeDelete: 1,
      isShowDeleteWhiteLabelModal: false,
      isShowCreateWhiteLabelConfirm: false,
    }
  },
  computed: {
    breadcrumbText() {
      return this.$i18nPresenter.t('White Label')
    },
    placeHolderEGLiveDomain() {
      return this.$i18nPresenter.t('E.g. live.yourdomain.com')
    },
    placeHolderEGPresenter() {
      return this.$i18nPresenter.t('E.g. presenter.yourdomain.com')
    },
  },
  watch: {
    '$store.state.currentUser': function watchCurrentUser() {
      if (this.$store.state.currentUser.id) {
        if (this.$store.state.currentUser.plan !== 'white-label') {
          this.$router.push('/apps/presentations')
          return
        }
        this.whiteLabelLink = this.$store.state.currentUser.whiteLabelLink
        this.whiteLabelPresenterDomain = this.$store.state.currentUser.whiteLabelPresenterDomain
      }
    },
  },
  created() {
    this.actions = {
      UPDATE_AUDIENCE_DOMAIN: 1,
      UPDATE_PRESENTER_DOMAIN: 2,
      DELETE_PRESENTER_DOMAIN: 3,
      DELETE_AUDIENCE_DOMAIN: 4,
    }
  },
  mounted() {
    if (this.$store.state.currentUser.id) {
      if (this.$store.state.currentUser.plan !== 'white-label') {
        this.$router.push('/apps/presentations')
        return
      }
      this.whiteLabelLink = this.$store.state.currentUser.whiteLabelLink
      this.whiteLabelPresenterDomain = this.$store.state.currentUser.whiteLabelPresenterDomain
    }
  },
  methods: {
    cancelDelete() {
      this.isShowDeleteWhiteLabelModal = false
    },
    confirmDelete() {
      this.isShowDeleteWhiteLabelModal = false
      if (this.typeDelete === 1) {
        this.updateProfile(this.actions.DELETE_AUDIENCE_DOMAIN)
      } else if (this.typeDelete === 2) {
        this.updateProfile(this.actions.DELETE_PRESENTER_DOMAIN)
      }
    },
    showModalDelete(type) {
      this.typeDelete = type
      this.isShowDeleteWhiteLabelModal = true
    },
    ok() {
      this.isShowCreateWhiteLabelConfirm = false
    },
    setLoadingIndicator(type) {
      if (type === this.actions.UPDATE_AUDIENCE_DOMAIN) {
        if (this.isSubmittingAudienceDomain) return
        this.isSubmittingAudienceDomain = true
        this.audienceDomainError = ''
      } else if (type === this.actions.UPDATE_PRESENTER_DOMAIN) {
        if (this.isSubmittingPresenterDomain) return
        this.presenterDomainError = ''
        this.isSubmittingPresenterDomain = true
      } else if (type === this.actions.DELETE_PRESENTER_DOMAIN) {
        this.isDeletingWhiteLabelPresenter = true
      } else if (type === this.actions.DELETE_AUDIENCE_DOMAIN) {
        this.isDeletingWhiteLabelAudience = true
      }
    },
    async updateProfile(type) {
      // Prepare payload to be sent to API
      const payload = {}
      this.typeUpdate = type
      if (type === this.actions.UPDATE_AUDIENCE_DOMAIN) {
        payload.whiteLabelLink = this.whiteLabelLink
        if (payload.whiteLabelLink && !payload.whiteLabelLink.trim()) {
          // eslint-disable-next-line sonarjs/no-duplicate-string
          this.audienceDomainError = this.$i18nPresenter.t('Invalid data')
          return
        }
        if (
          payload.whiteLabelLink ===
          this.$store.state.currentUser.whiteLabelPresenterDomain
        ) {
          this.audienceDomainError = this.$i18nPresenter.t('Invalid data')
          return
        }
      } else if (type === this.actions.UPDATE_PRESENTER_DOMAIN) {
        payload.whiteLabelPresenterDomain = this.whiteLabelPresenterDomain
        if (
          payload.whiteLabelPresenterDomain &&
          !payload.whiteLabelPresenterDomain.trim()
        ) {
          this.presenterDomainError = this.$i18nPresenter.t('Invalid data')
          return
        }
        if (
          payload.whiteLabelPresenterDomain ===
          this.$store.state.currentUser.whiteLabelLink
        ) {
          this.presenterDomainError = this.$i18nPresenter.t('Invalid data')
          return
        }
      } else if (type === this.actions.DELETE_PRESENTER_DOMAIN) {
        payload.isDeletePresenterDomain = true
      }
      if (type === this.actions.DELETE_AUDIENCE_DOMAIN) {
        payload.isDeleteAudienceDomain = true
      }

      this.setLoadingIndicator(type)

      // Update user via API
      const userId = this.$store.state.currentUser.id
      try {
        await UserApi.patchUserById(userId, payload)
        this.isShowCreateWhiteLabelConfirm = true
        // Update name
        Util.mixpanelTracking(
          'User',
          'Update Account',
          'User update whitelabel link',
        )
        if (type === this.actions.UPDATE_AUDIENCE_DOMAIN) {
          this.audienceDomainError = ''
          this.isSubmittingAudienceDomain = false
          this.$store.state.currentUser.whiteLabelLink = this.whiteLabelLink
        } else if (type === this.actions.UPDATE_PRESENTER_DOMAIN) {
          this.presenterDomainError = ''
          this.isSubmittingPresenterDomain = false
          this.$store.state.currentUser.whiteLabelPresenterDomain = this.whiteLabelPresenterDomain
        } else if (type === this.actions.DELETE_PRESENTER_DOMAIN) {
          this.presenterDomainError = ''
          this.isDeletingWhiteLabelPresenter = false
          this.$store.state.currentUser.whiteLabelPresenterDomain = ''
          this.whiteLabelPresenterDomain = ''
        } else if (type === this.actions.DELETE_AUDIENCE_DOMAIN) {
          this.presenterDomainError = ''
          this.isDeletingWhiteLabelAudience = false
          this.$store.state.currentUser.whiteLabelLink = ''
          this.whiteLabelLink = ''
        }
      } catch (err) {
        Sentry.captureException(err)
        if (err.body.message) {
          if (type === this.actions.UPDATE_AUDIENCE_DOMAIN) {
            this.audienceDomainError = err.body.message
            this.isSubmittingAudienceDomain = false
          } else if (type === this.actions.UPDATE_PRESENTER_DOMAIN) {
            this.presenterDomainError = err.body.message
            this.isSubmittingPresenterDomain = false
          } else if (type === this.actions.DELETE_PRESENTER_DOMAIN) {
            this.isDeletingWhiteLabelPresenter = false
            this.presenterDomainError = err.body.message
          } else if (type === this.actions.DELETE_AUDIENCE_DOMAIN) {
            this.isDeletingWhiteLabelAudience = false
            this.presenterDomainError = err.body.message
          }
          Util.mixpanelTracking(
            'User',
            'Update Account',
            'Update Account Failed',
          )
        } else if (type === this.actions.UPDATE_AUDIENCE_DOMAIN) {
          this.audienceDomainError = JSON.stringify(err.body.errors)
          this.isSubmittingAudienceDomain = false
        } else if (type === this.actions.UPDATE_PRESENTER_DOMAIN) {
          this.presenterDomainError = JSON.stringify(err.body.errors)
          this.isSubmittingPresenterDomain = false
        }

        // Network errors (no connections, dns error, etc.)
        if (err.status === 0) {
          if (type === this.actions.UPDATE_AUDIENCE_DOMAIN) {
            this.audienceDomainError = this.$i18nPresenter.t('Something went wrong when updating domain. Please try again!')
            this.isSubmittingAudienceDomain = false
          } else if (type === this.actions.UPDATE_PRESENTER_DOMAIN) {
            this.presenterDomainError = this.$i18nPresenter.t('Something went wrong when updating domain. Please try again!')
            this.isSubmittingPresenterDomain = false
          } else if (type === this.actions.DELETE_PRESENTER_DOMAIN) {
            this.presenterDomainError = this.$i18nPresenter.t('Something went wrong when deleting domain. Please try again!')
            this.isDeletingWhiteLabelPresenter = false
          } else if (type === this.actions.DELETE_AUDIENCE_DOMAIN) {
            this.audienceDomainError = this.$i18nPresenter.t('Something went wrong when deleting domain. Please try again!')
            this.isDeletingWhiteLabelAudience = false
          }
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.white-label-content {
  @apply w-full md:w-2/3 flex flex-col justify-center md:mx-auto;
  max-width: 1280px;
}
</style>
